import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { isAfter } from 'date-fns';

import BreadCrumb from 'src/components/Retail/BreadCrumb/BreadCrumb';
import RetailPage from 'src/containers/Page/RetailPage';
import CancelTicket from '../CancelTicket/CancelTicket';
import TicketDetails from '../TicketDetails/TicketDetails';

interface TicketProps {
  isCancel?: boolean,
  isGetTicket?: boolean,
  isPayout?: boolean
  isCancelRequest?: boolean
}

function Ticket({ isCancel=false, isGetTicket=false, isPayout=false, isCancelRequest=false }: TicketProps) {
  const [ticketDetails, setTicketDetails] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isCancelable, setIsCancelable] = useState(false);
  const [isPayoutable, setIsPayoutable] = useState(false);
  const [showBetslip, setShowBetslip] = useState(false);

  const query = new URLSearchParams(location.search);
  const code = query.get('code');
  const payout = query.get('payout');
  const cancel = query.get('cancel');

  const setDetails = useCallback((details) => {
    setTicketDetails(details);
  }, []);

  const presentBetslip = useCallback((value) => {
    setShowBetslip(value);
  }, []);

  const updateIsCancellable = useCallback((cancel) => {
    setIsCancelable(cancel);
  }, []);

  const updateIsPayoutable = useCallback((payout) => {
    setIsPayoutable(payout);
  }, []);

  const getTicketDetails = async (ticket) => {
    const details = await dispatch.peripherals.getTicketDetails({ ticket });

    if (details && _.size(details) > 0 && !isCancel){
      setDetails(details);

      const cancelTicket = details.isCancellable && isAfter(new Date(), details.cancellableUntil);
      const payoutTicket = details.statusName === 'settled' && details.netReturn > 0 && details.paidOut < details.netReturn;
      setIsCancelable(cancelTicket);
      setIsPayoutable(payoutTicket);

      if (payout && payoutTicket){
        history.replace(`#payout-ticket?code=${ code }`);
      } else if (cancel && cancelTicket){
        history.replace(`#cancel-ticket?code=${ code }`);
      }
    }
  };

  useEffect(() => {
    if (code){
      getTicketDetails(code);
    }
  }, [code]);

  const breadcrumbs = () => {
    let display = [{ name: t('retail.home'), link:'/' }];
    if (isCancel){
      display.push({ name: t('home.cancelTicket'), link: ''  });
    } else if (isGetTicket && !ticketDetails){
      display.push({ name: t('home.getTicket'), link: ''  });
    } else if (isGetTicket && ticketDetails){
      display.push();
      display = [...display, { name: t('home.getTicket'), link: '/get-ticket'  }, { name: t('ticketDetails.ticketDetails'), link: '' }];
    } else {
      display.push({ name: t('home.payoutTicket'), link: '' });
    }
    return display;
  };

  return (
    <RetailPage includeBetslip={ showBetslip }>
      <BreadCrumb breadcrumbs={ breadcrumbs() } />
      <div className="ticket">
        {
          ticketDetails && !isCancel && !isPayout ? <TicketDetails
            details={ ticketDetails }
            setDetails={ setTicketDetails }
            isCancelable={ isCancelable }
            isPayoutable={ isPayoutable }
            presentBetslip= { presentBetslip }
            updateIsCancellable={ updateIsCancellable }
            updateIsPayotable={ updateIsPayoutable }
          /> :
            <CancelTicket
              isCancelRequest= { isCancelRequest }
              isCancel={ isCancel }
              updateIsCancellable={ updateIsCancellable }
              updateIsPayotable={ updateIsPayoutable }
              setDetails={ setDetails }
              isPayout={ isPayout }
            />
        }
      </div>
    </RetailPage>
  );
}

export default Ticket;
